import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, type ReactNode } from "react";

// Lazy — Sonner (~15KB gzip) is only needed after user interactions (form
// submits, copy-to-clipboard). Keeping it out of the main chunk trims LCP.
const Toaster = lazy(() =>
  import("sonner").then((m) => ({ default: m.Toaster })),
);

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import logoAsset from "../assets/logo-512.png.asset.json";

const PUBLISHER_LOGO_URL = `https://mapobserver.com${logoAsset.url}`;

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">404</p>
        <h1 className="headline-display mt-3 text-5xl">Page not found</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          The story you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-ochre-deep transition-colors"
          >
            Return to the homepage
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="headline-display text-3xl">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. Try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center bg-foreground px-4 py-2 text-sm font-medium text-background hover:bg-ochre-deep transition-colors"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-paper-soft transition-colors"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Map Observer — The authority on Google Maps, Business Profile & local search" },
      {
        name: "description",
        content:
          "The authoritative newsroom on Google Maps, Google Business Profile, and local SEO — read daily by operators, agencies, and in-house teams.",
      },
      { name: "author", content: "Map Observer" },
      { name: "theme-color", content: "#f5f1e8" },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "Map Observer" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@mapobserver" },
      { name: "google-site-verification", content: "wvs0xgkEFcCeGajc2JpOQdnpaLm4y199wk8Vo9uXYk0" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Organization", "NewsMediaOrganization"],
          "@id": "https://mapobserver.com/#organization",
          name: "Map Observer",
          url: "https://mapobserver.com",
          logo: {
            "@type": "ImageObject",
            url: PUBLISHER_LOGO_URL,
            width: 512,
            height: 512,
          },
          description:
            "The authoritative newsroom covering Google Business Profile, the Map Pack, and local search for SEO agencies and local business owners.",
          sameAs: ["https://twitter.com/mapobserver"],
          funder: {
            "@type": "Organization",
            name: "GBPly",
            url: "https://gbply.net",
            slogan: "A Smarter Way to Manage Google Business Profiles",
          },
          sponsor: {
            "@type": "Organization",
            name: "GBPly",
            url: "https://gbply.net",
          },
          publishingPrinciples: "https://mapobserver.com/editorial-standards",
          ethicsPolicy: "https://mapobserver.com/ethics",
          ownershipFundingInfo: "https://mapobserver.com/ownership-and-funding",
          correctionsPolicy: "https://mapobserver.com/corrections",
          knowsAbout: [
            "Local SEO",
            "Google Business Profile",
            "Google Maps",
            "Map Pack",
            "Local 3-Pack",
            "AI Overviews",
            "AI Mode",
            "Ask Maps",
            "Google Local Service Ads",
            "Reviews and reputation management",
          ],
          audience: {
            "@type": "BusinessAudience",
            audienceType:
              "Local SEO agencies, Google Business Profile specialists, multi-location operators, and local business owners",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Map Observer",
          url: "https://mapobserver.com",
          inLanguage: "en",
          description:
            "The daily briefing on Google Business Profile, the Map Pack, and local search — for local SEO agencies and the operators they serve.",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://mapobserver.com/?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico" },
      { rel: "alternate", type: "application/rss+xml", title: "Map Observer", href: "https://mapobserver.com/rss.xml" },
      { rel: "preconnect", href: "https://pzerlxacfqceurqguelq.supabase.co", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://pzerlxacfqceurqguelq.supabase.co" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-foreground focus:text-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium"
        >
          Skip to main content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Suspense fallback={null}>
        <Toaster richColors closeButton position="bottom-right" />
      </Suspense>
    </QueryClientProvider>
  );
}
